import Backbone from "backbone";
import _ from "underscore";
import * as logger from "./utils/logger";
import { shouldAddExtraLogging } from "./utils/logger.internal";
import { handleApi, handleJSNative } from "./utils/error";
import constants, { HOURS, MINUTES } from "./constants";
import { dispatcher, DispatcherEvents } from "./utils/dispatcher";
import scheduler, { UPLOAD_EVENT_LOGS_TASK } from "./utils/scheduler";
import Context from "./app.context";
import PromiseDebounce from "./utils/promiseDebounce";
import { UserService } from "./utils/users";
import { SyncType } from "./sync/sync.types";
import { ICredentialsWithServer } from "./utils/credentialsStore";
import { getCurrentVehicle } from "./utils/device";
import Movement from "./services/sharedServices/movement";
import { isEmpty } from "./utils/vanilla";
import { CancelablePromise as Promise } from "./utils/cancelablePromise";
import { DriveStore } from "./store/reducers";
import { getServerDate } from "./utils/storage/date";
import { DataActions } from "./store/data/actions";
import { Router } from "./router";
import DutyStatusLog from "./model/DutyStatusLog";
import { UserHosRuleSetActions } from "./store/userHosRuleSet/actions";
import { cloneStorageToDictionary, convertStorageRulesetsToDictionary } from "./utils/helpers";
import { actions } from "./store/users/actions";
import { IVehicleDrivingEvent } from "./services/serverEvents";
import { syncsForLogin } from "./sync/sync";
import { DvirLogActions } from "./store/dvirLogs/actions";
import { ShipmentActions } from "./store/shipment/actions";
import { getShipments, getUpdatedGroupedShipments } from "./model/Shipments";
import { DeviceMovementActions } from "./store/deviceMovement/actions";
import { IFeatureFlagsService } from "./services/featureFlags";
import { getProxiedOpenCabService, OpenCabService } from "./services/openCab";
import { isIamLogin } from "./utils/uri";
import { ScheduleStatusActions } from "./store/scheduleStatus/actions";
import { checkRedirectRequired } from "./utils/redirectioner";
import PerformanceProfilerFactory from "./utils/performanceProfiler/performanceProfilerFactory";
import { NetworkStates } from "./store/mobile/types";
import { observeStore } from "./utils/store";
import { clearExpiredImageCache } from "./ui/dvir/imageHelper";

export enum AppManagerEvents {
    // Triggered when a context is removed from the active contexts
    ContextRemoved = "context:removed"
}

interface LogEvent {
    verb: string;
    data: string[];
}

export type TContextSource = "getTempContext" | "getContext";

export default class AppManager {

    movement: Movement;
    events: Backbone.IEvents;
    store: DriveStore;
    router: Router;
    openCabService?: OpenCabService;
    private readonly activeContexts: { [userName: string]: Context };
    private tempContext: Context | null;
    private readonly coDriverLogTasks = new WeakMap<Context, Promise>();

    constructor(store: DriveStore, router: Router, private readonly featureFlagsService: IFeatureFlagsService, readonly userService: UserService) {
        this.store = store;
        this.activeContexts = {};
        this.tempContext = null;
        this.movement = new Movement(this.store, this.featureFlagsService);
        this.events = _.clone(Backbone.Events);
        this.router = router;

        // Create and cache Contexts for each active user (primary driver and co-driver(s))
        // AppManager should only be constructed once at app startup
        (this.userService.getActiveUserNames() || []).forEach(userName => {
            this.getContext(userName);
        });

        try {
            this.openCabService = getProxiedOpenCabService(this.store, this.userService, this.getActiveUserContext.bind(this), this.router);
        } catch (e: unknown) {
            logger.error(logger.Source.OPENCAB, `Failed to initialize OpenCabService. ${ e instanceof Error ? e.message : undefined }`);
        }

        this.movement.trackMovement();
        this.setupMonitorPosition();

        // Runs the clear outdated address cache method every 30 minutes.
        scheduler.registerTask("@clearOutdatedAddressCache", () => this.clearOutdatedAddressCache(), constants.CLEAR_ADDRESS_CACHE).start("@clearOutdatedAddressCache");

        /* eslint-disable @typescript-eslint/unbound-method */
        dispatcher.on(DispatcherEvents.UserAddDSLForDriver, this.addDutyStatusLog, this);
        dispatcher.on(DispatcherEvents.UserSetDSLForDriver, this.setDutyStatusLog, this);
        dispatcher.on(DispatcherEvents.DriverSwitch, this.onSwitchActiveDriver, this);
        /* eslint-enable @typescript-eslint/unbound-method */
        PerformanceProfilerFactory.featureFlagsService = featureFlagsService;
    }

    // Used by openCab
    getActiveUserContext(userName?: string): Context {
        let activeUser = typeof userName === "string" ? userName.toLowerCase() : this.userService.getActiveUserNames()[0] as string | undefined;

        if (activeUser && this.activeContexts.hasOwnProperty(activeUser)) {
            return this.activeContexts[activeUser];
        }
        // Log if we can't find an existing context for the provided active user, only if there is an active user.
        // If there's no active user then either 1) called before login or 2) app will be failing in other ways because there are no active users...
        activeUser && logger.error(logger.Source.OPENCAB, `Unable to get an existing context for activeUser: ${ activeUser }, userName: ${ userName }.`);
        return this.getContext(userName);
    }

    /**
     * Gets a cached Context for a user.
     * @param userName The username to get the cached Context for
     * @returns A Context object if the user context is present in the activeContexts, otherwise undefined
     */
    getCachedContext(userName: string): Context | undefined {
        let activeUser = typeof userName === "string" ? userName.toLowerCase() : this.userService.getActiveUserNames()[0] as string | undefined;

        if (activeUser && this.activeContexts.hasOwnProperty(activeUser)) {
            return this.activeContexts[activeUser];
        }
        return undefined;
    }

    /**
     * Gets or creates a Context, which is a collection of user-specific services that are "hot-swapped" into the UI when switching users.
     * If there is already a logged in user, their Context is returned.
     * @param userName The username to get/create a Context for. If undefined, return the Context of the currently active UI user.
     * @param credentials If provided, override the Context's API credentials. Used for first login or session expiry
     * @returns A Context object
     */
    getContext(userName?: string, credentials?: ICredentialsWithServer) {
        let activeUser = typeof userName === "string" ? userName.toLowerCase() : this.userService.getActiveUserNames()[0] as string | undefined;

        let context: Context;
        if (activeUser && this.activeContexts.hasOwnProperty(activeUser)) {
            context = this.activeContexts[activeUser];
        } else {
            if (shouldAddExtraLogging(logger.Source.IAM)) {
                logger.log(logger.Source.IAM, `AppManager.getContext: Creating new context for user ${ activeUser || "None" }.`);
            }
            context = new Context({ manager: this }, this.featureFlagsService, activeUser, "getContext");
        }

        // Override the credentials of the API service
        credentials && context.api.setUser(credentials.credentials, credentials.server, credentials.iamSettings);

        // TODO: Re-architect the movement service so that this is not required
        this.movement.changeMovementContext(context);

        // Start services/scheduler tasks and add this user's Context to the cache
        if (activeUser) {
            if (context.api.getCurrentCredentials() || context.api.getUser()) {
                let user = context.api.getUser();
                if (!user) {
                    let activeUserError = new Error("Active user not created");

                    // Only manually send if it's on bootloader as when AppView is initialized we get full logging already
                    let isBootloader = window.driveApp === undefined;
                    if (isBootloader) {
                        context.errorHandler.handleJSNativeImpl(activeUserError, true, "bootloader");

                        // Only attempt mitigation steps when crashes happen at bootloader
                        // Crashes that happen after AppView intiailization are recoverable
                        this.store.dispatch(actions.setUsers({}));
                        context.api.forget();
                    }

                    throw activeUserError;
                }
                this.userService.addActiveUser(activeUser);

                if (!(isIamLogin() && checkRedirectRequired(context))) {
                    this.startSchedulesAndServices(context, activeUser);
                }

                this.activeContexts[activeUser] = context;
            } else {
                // Silent until MYG-143880: this classification decides whose per-user state a later
                // RemoveUserSessions sweep clears, so leaving no trace made incidents unattributable.
                logger.log(logger.Source.USER, `getContext: no credentials or user for ${ activeUser }; classifying inactive.`);
                this.userService.removeActiveUser(activeUser);
            }
        }

        // We create a "context" n first app load that should be removed
        this.removeTempContext();

        // Should remove any inactive users and any associated keys
        dispatcher.trigger(DispatcherEvents.RemoveUserSessions);

        return context;
    }

    /**
     * Returns a ephemeral Context. The design of the app always requires a Context to be active in the UI,
     * so on SSO login or when adding a co-driver, we create a temporary context on the login screen.
     * When adding a co-driver, this prevents the previous user's Context from getting overridden with new credentials.
     * Once a proper Context is retrieved, this Context gets destroyed and cleaned up.
     * @param credentials If provided, override the Context's API credentials. Used for first login or session expirty.
     * @return A temporary Context object
     */
    getTempContext(credentials?: ICredentialsWithServer): Context {
        if (shouldAddExtraLogging(logger.Source.IAM)) {
            logger.log(logger.Source.IAM, `AppManager.getTempContext: Creating temporary context for user ${ credentials ? credentials.credentials.userName : "None" }.`);
        }
        this.removeTempContext();
        let tempContext = new Context(
            { manager: this },
            this.featureFlagsService,
            credentials ? credentials.credentials.userName : undefined,
            "getTempContext"
        );
        credentials && tempContext.api.setUser(credentials.credentials, credentials.server, credentials.iamSettings);
        this.tempContext = tempContext;
        return tempContext;
    }

    /**
     * Dispatched the data reducer to run an action that clears any cached addresses that are older than 2 weeks.
     */
    clearOutdatedAddressCache = () => {
        let serverDate = getServerDate();
        serverDate.setDate(serverDate.getDate() - 14);
        this.store.dispatch(DataActions.clearAddressCache(serverDate));
    };

    serverEventSyncHos(e: { userName: string}) {
        scheduler.fire("@sync_hos", e.userName);
    }

    enableSyncTasks(userName: string) {
       scheduler.startForUser(userName);

        let context = this.activeContexts[userName];
        if (context) {
            this.enableServerEvents(context);
        }
    }

    disableSyncTasks(userName: string) {
        scheduler.stopForUser(userName);

        let context = userName && this.activeContexts[userName];
        if (context) {
            this.disableServerEvents(context);
        }
    }

    /**
     * Destroy a Context and remove it from the cache
     * @param context The Context to remove
     */
    unregister(context: Context, removeCredentials = false, skipNeedsCredentials = false) {
        if (!context.user) {
            return;
        }

        let userName = context.user;
        this.userService.removeActiveUser(userName);
        scheduler.unregisterAll(userName);

        if (this.activeContexts.hasOwnProperty(userName)) {
            if (Object.keys(this.activeContexts).length === 1) {
                scheduler.unregisterTask("@systemSettings");
                scheduler.unregisterTask("@clearExpiredImageCache");
                scheduler.unregisterTask("@clockDrift");
                scheduler.unregisterTask("@versionCheck");
            }
            removeCredentials && this.activeContexts[userName].api.forget(undefined, undefined, skipNeedsCredentials);
            this.activeContexts[userName].destroy(true);
            delete this.activeContexts[userName];
            this.events.trigger(AppManagerEvents.ContextRemoved, userName);
        }
    }

    /**
     * Destroy a Context by a user name, and remove it from the cache
     * @param username The user name of the Context to remove
     */
    unregisterByUsername(username: string | null, removeCredentials = false, skipNeedsCredentials = false): void {
        if (username) {
            let context = this.activeContexts[username.toLowerCase()];
            context && this.unregister(context, removeCredentials, skipNeedsCredentials);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onVehicleSelected(_appContext: Context, currentDevice: IIdEntity, previousDevice?: IIdEntity | null, syncLater?: boolean, overrideDate?: Date) {
        this.store.dispatch(DataActions.setVehicleLocation(undefined));
        let promises: Promise[] = [];
        let currentUser = this.userService.getCurrentUser();
        this.applyActionToAllContexts((user, context) => {
            context.sync.onSelectNewVehicle(currentDevice, previousDevice);
            if (user?.id === currentUser?.id) {
                const previousWork = this.coDriverLogTasks.get(context) || Promise.resolve();
                promises.push(previousWork.then(() => Promise.all(context.vehicleService.createLoginLogoffLogs(user, currentDevice, overrideDate, syncLater))));
            } else {
                this.tryCreateCoDriverLogs(context, currentDevice, overrideDate, { allowEmptyHistory: true, syncLater: false });
            }
        });
        return Promise.all(promises);
    }

    tryCreateCoDriverLogs(
        context: Context,
        device: IIdEntity,
        dateTime?: Date,
        options: { allowEmptyHistory?: boolean; syncLater?: boolean } = {}
    ): void {
        const { allowEmptyHistory = false, syncLater = true } = options;
        const user = context.api.getUser();
        if (!user || !context.api.getCurrentCredentials()
            || (!allowEmptyHistory && !context.dutyStatusLogService.hasUserLogs(user.id))) {
            return;
        }
        const reportError = error => logger.error(logger.Source.ERROR, "Encountered an error creating login/logoff logs.", user, error);
        // Queue behind any unfinished attempt instead of dropping this one, so the latest vehicle is not lost.
        const previousWork = this.coDriverLogTasks.get(context) || Promise.resolve();
        const task = previousWork
            .then(() => {
                // Checked when the attempt runs, so a queued older selection cannot backdate over a newer session.
                const latestSession = context.vehicleService.getLastLoginLogoffLogForUser(user);
                if (dateTime && latestSession && latestSession.getDate().getTime() > dateTime.getTime()) {
                    return;
                }
                return Promise.all(context.vehicleService.createLoginLogoffLogs(user, device, dateTime, syncLater).map(log => log.catch(reportError)));
            })
            .catch(reportError)
            .then(() => { this.coDriverLogTasks.get(context) === task && this.coDriverLogTasks.delete(context); });
        this.coDriverLogTasks.set(context, task);
    }

    syncHos(context: Context, userName: string, isScheduled?: boolean) {
        context.dutyStatusLogDebounce?.run(resolve => {
            this.sync(context, [SyncType.HOS], isScheduled).then(() => {
                    this.syncHosCallback(context, userName);
                    resolve();
                })
                .catch(handleApi);
        });
    }

    syncHosCallback(context: Context, userName: string) {
        scheduler.fire("@hosRules", userName);
        let currentVehicle = getCurrentVehicle();
        if (currentVehicle && !scheduler.isTaskRegistered("@dtcMonitor", userName)) {
            //Do nothing if its EWD
            if (this.store.getState().data.ELDJurisdiction === "EWD") {
                return;
            }
            // When running DTCMonitor related unit tests, set runLater flag to false, so that diagnostic logs are created instantly
            let debugMode: boolean | undefined = window.DRIVE_DEBUG && window.DRIVE_DEBUG.ENABLE_ELD_TRANSFER;
            let interval = constants.DTC_MONITOR;
            let apiUser = this.userService.getUserByName(userName);
            let apiUserId  = apiUser ? apiUser.id : undefined;

            if (!debugMode && apiUserId) {
                let dtcNextRun = this.store.getState().data.dtcNextRun[apiUserId];
                const RANDOM_INTERVAL = Math.floor(Math.random() * 60) * 60 * 1000;
                interval = window.DRIVE_DEBUG?.STARTING_INTERVAL || RANDOM_INTERVAL;
                if (dtcNextRun) {
                    interval = new Date(dtcNextRun).getTime() - getServerDate().getTime();
                    if (interval < 0) {
                        debugMode = true;
                        interval = constants.DTC_MONITOR;
                    } else if (interval > HOURS) {
                        // Defensive: the only legitimate writers of dtcNextRun are this function's
                        // own RANDOM_INTERVAL jitter (up to ~59 min, just above) and DTCMonitor's
                        // 15-minute NoDeviceId retry reschedule — the real-vehicle 24h reschedule no
                        // longer persists dtcNextRun at all. So a persisted value implying more than
                        // an hour is stale/corrupted (e.g. clock drift, leftover pre-fix data), not
                        // legitimate. Disregard it and fall back to a fresh short interval instead of
                        // trusting an unexpectedly large wait.
                        interval = window.DRIVE_DEBUG?.STARTING_INTERVAL || RANDOM_INTERVAL;
                    }
                }
            }

            scheduler
                .registerTask("@dtcMonitor", () => context.dtcMonitor.runDTCTransfer(), interval, userName, !debugMode)
                .start("@dtcMonitor", userName);

            if (!debugMode && apiUserId) {
                this.store.dispatch(
                    DataActions.setDtcNextRun(
                        { [apiUserId]: (scheduler.getNextRunDate("@dtcMonitor", userName) || getServerDate()).toISOString() }
                    )
                );
            }
        }

        // Run vehicle proximity after auto HOS sync.
        dispatcher.trigger(DispatcherEvents.RunVehicleProximity);
    }

    startUploadEventLogsTask(context: Context, userName: string | undefined) {
        let startingInterval = window.DRIVE_DEBUG?.STARTING_INTERVAL ||
            Math.floor(Math.random() * 5 * MINUTES + 2 * MINUTES);
        scheduler
            .registerTask(
                UPLOAD_EVENT_LOGS_TASK,
                () => context.sync.eventLogs.upload(false),
                constants.RAPID_SYNC_EVENTS,
                userName,
                true,
                false,
                false,
                startingInterval
            )
            .start(UPLOAD_EVENT_LOGS_TASK, userName);
    }

    /**
     * Removes the ephemeral Context.
     */
    private removeTempContext(): void {
        if (this.tempContext !== null) {
            this.tempContext && this.tempContext.destroy(false);
            this.tempContext = null;
        }
    }

    /**
     * Starts services and scheduled tasks for a Context
     * @param context The Context to start services for
     * @param userName The user
     */
    private startSchedulesAndServices(context: Context, userName: string): void {
        let startingInterval = window.DRIVE_DEBUG?.STARTING_INTERVAL || Math.floor((Math.random() * 5 * 60 + 30) * 1000);

        if (!context.api.getCurrentCredentials()) {
            // The user's session has expired, so we run only the user's network service,
            // which makes an api call with invalid creds and triggers the session expiry login workflow.
            scheduler
                .registerTask("@networkBridge", () => context.network.checkBridge(), constants.BRIDGE.REFRESH_INTERVAL, userName, false, false, false, startingInterval)
                .start("@networkBridge", userName);
            return;
        }

        let apiUserId = context.api.getUser()?.id;
        if (
            apiUserId &&
            this.store.getState().scheduleStatus[apiUserId]
        ){
            logger.log(logger.Source.SERVICES, "startSchedulesAndServices: Already initialized schedules", userName);
            return;
        }
        context.dutyStatusLogDebounce = context.dutyStatusLogDebounce
            || new PromiseDebounce(msg => logger.log(logger.Source.SERVICES, msg), msg => logger.error(logger.Source.SERVICES, msg), handleJSNative);
        context.tripsDebounce = context.tripsDebounce || new PromiseDebounce(msg => logger.log(logger.Source.SERVICES, msg), msg => logger.error(logger.Source.SERVICES, msg), handleJSNative);

        let syncTrips = () => {
            context.tripsDebounce?.run(resolve => {
                this.sync(context, [SyncType.Trip, SyncType.TripAnnotation, SyncType.GroupLinkedDevice])
                    .then(() => resolve())
                    .catch(handleApi);
            });
        };

        let syncDriverChange = () => {
            this.sync(context, [SyncType.DriverChange]).catch(handleApi);
        };

        let startingTasks = ["@clearUnusedAddressCache", "@sync", "@hosRules",
                             "@networkBridge", "@movementEnsure", "@systemSettings",
                             "@checkDriverChanges", "@sync_hos", "@sync_defectlists", "@newsNotifications", "@featureFlags",
                             "@sessionExpiry", "@userRefresh", "@wsPing"];
        let enableSyncDelay = this.store.getState().data.enableSyncDelay ?? true;

        scheduler
            .registerTask("@networkBridge", () => context.network.checkBridge(), constants.BRIDGE.REFRESH_INTERVAL, userName, false, false, false, startingInterval)
            .registerTask("@sync", () => this.sync(context), constants.SYNC, userName, enableSyncDelay, false, false, startingInterval)
            // Global singleton tasks: registered without a userName so unregisterAll(user) cannot remove them.
            // Closures use getSharedTaskContext() to always execute against the currently active context,
            // avoiding stale-closure issues when the user who first registered them logs out.
            .registerTask("@hosRules", () => context.hosProcess.process(), constants.HOS.RULE_CALCULATION_INTERVAL, userName)
            .registerTask("@movementEnsure", () => context.manager.movement.ensureWatchPosition(), constants.DRIVING.TRACKING, userName)
            .registerTask("@clockDrift", () => this.getSharedTaskContext()?.databaseCache.getClockDrift(), constants.CLOCK_DRIFT_INTERVAL, undefined, true, true, false, startingInterval)
            .registerTask("@checkDriverChanges", syncDriverChange, constants.DRIVER_CHANGE_INTERVAL, userName, true, false, false, startingInterval, true)
            .registerTask("@sync_hos", isScheduled => this.syncHos(context, userName, isScheduled), constants.SYNC_HOS_INTERVAL, userName, true, false, false, startingInterval, true)
            .registerTask("@sync_defectlists", () => context.sync.misc.syncDefectLists(true), constants.SYNC_DEFECTLISTS, userName, true, false, false, startingInterval)
            .registerTask("@newsNotifications", () => context.newsService.check(), constants.NEWS_CHECK_INTERVAL, userName, true, false, false, startingInterval)
            .registerTask("@versionCheck", () => this.getSharedTaskContext()?.databaseCache.checkSourceCodeUpdate(), constants.CACHE.REFRESH_INTERVAL, undefined, true, true, false, startingInterval)
            .registerTask("@syncTrips", syncTrips, constants.ON_DEMAND_INTERVAL, userName, false, false, true)
            .registerTask("@featureFlags", () => {
                context.featureFlagsService.downloadFeatureFlags.bind(context.featureFlagsService)(context.api);
            }, constants.FEATURE_FLAG_INTERVAL, userName, true, false, false)
            .registerTask("@sessionExpiry", () => context.sessionService.checkExpiry(), constants.SESSION.CHECK_EXPIRY_INTERVAL, userName, false, false, false, startingInterval)
            .registerTask("@userRefresh", () => context.serverEvents.refreshCurrentUser(), 6 * HOURS, userName, true, false, false, startingInterval)
            .registerTask("@wsPing", () => {
                const mobileState = this.store.getState().mobile;
                context.pingService.tick(mobileState.online === NetworkStates.ONLINE);
            }, constants.WEBSOCKETS.PING_INTERVAL_MS, userName, true, false, false, startingInterval);


        scheduler.start(startingTasks, userName);
        // Start the global singleton tasks (registered without a userName, so startForUser skips them).
        scheduler.start(["@clockDrift", "@versionCheck"]);

        this.startUploadEventLogsTask(context, userName);

        // Register and start global SystemSettings task.
        scheduler.registerTask(
            "@systemSettings",
            () => this.getSharedTaskContext()?.databaseCache.getSystemSettings(),
            constants.SYSTEM_SETTINGS_INTERVAL,
            undefined,
            true,
            true,
            false,
            startingInterval,
            true
        ).start("@systemSettings");

        // Runs the clear expired image cache method every 20 minutes (matches cache expiry time).
        scheduler.registerTask("@clearExpiredImageCache", () => clearExpiredImageCache(), 20 * MINUTES, undefined, false, true).start("@clearExpiredImageCache");

        // #####################################################
        // ||                                                 ||
        // || Starts scheduled tasks for Geotab Drive Modules ||
        // ||                                                 ||
        // #####################################################
        context.startTasks();

        this.enableServerEvents(context);

        if (apiUserId) {
            this.store.dispatch(ScheduleStatusActions.setScheduleStatus({ userId: apiUserId, isInitialized: true}));
            logger.log(logger.Source.SERVICES, "startSchedulesAndServices: finished initializing schedules", userName);
        }
    }

    private sync(context: Context, selectiveSync?: SyncType[], isScheduled?: boolean) {
        this.store.dispatch(DataActions.setEnableSyncDelay(false));
        logger.log(logger.Source.SERVICES, "sync: enableSyncDelay=false", context.api.getUser()?.name);
        let thisSelectiveSync = selectiveSync;

        // Do not do an hos sync on a full sync unless a vehicle has been chosen already (when adding co-drivers or reloading page)
        if (!thisSelectiveSync && !getCurrentVehicle()) {
            thisSelectiveSync = syncsForLogin;
        }

        return context.sync.synchronize(false, undefined, thisSelectiveSync, isScheduled === false ? "websocket" : "scheduler");
    }

    private syncMessages(context: Context, data: {messageContentType: string}) {
        // For text messages, we debounce sync to avoid calling it for every message 1 by 1.
        window.clearTimeout(context.textMessageTimeout);
        context.textMessageTimeout = setTimeout(() => {
            let allowedValues = ["Normal", "CannedResponse", "Location"];
            if (!isEmpty(data.messageContentType) && (allowedValues.indexOf(data.messageContentType) > -1)) {
                this.sync(context, [SyncType.Messages])
                    .then(() => {
                        dispatcher.trigger(DispatcherEvents.MessagesChange);
                    })
                    .catch(handleApi);
            }
        }, 1000);
    }

    private syncDvirLogs(context: Context, e: LogEvent) {
        this.sync(context, [SyncType.DVIR])
            .then(() => {
                if (e.verb === "Remove") {
                    let apiUser = context.api.getUser();
                    if (!apiUser) {
                        return;
                    }
                    let localDvirlogs = cloneStorageToDictionary(this.store.getState().dvirLogs[apiUser.id] || {});
                    let dvirLogId = e.data[0];
                    if (dvirLogId && localDvirlogs.hasOwnProperty(dvirLogId)) {
                        delete localDvirlogs[dvirLogId];
                        this.store.dispatch(DvirLogActions.setDvirLogs({userId: apiUser.id, logs: localDvirlogs}));
                        dispatcher.trigger(DispatcherEvents.DvirLogSet, dvirLogId, true);
                    }
                } else {
                    dispatcher.trigger(DispatcherEvents.SyncDvir);
                }
            })
            .catch(handleApi);
    }

    private syncTrailerAttachments(context: Context) {
        this.sync(context, [SyncType.TrailerAttachments])
            .catch(handleApi);
    }

    private syncShipmentLogs(context: Context, e: LogEvent) {
        this.sync(context, [SyncType.Shipments])
            .then(() => {
                if (e.verb === "Remove") {
                    const localShipmentLogs = getShipments(this.store);
                    const shipmentLogId = e.data[0];
                    if (shipmentLogId && localShipmentLogs && Object.keys(localShipmentLogs).length > 0) {
                        const shipmentLog = localShipmentLogs[shipmentLogId];
                        const deletedDeviceId = (shipmentLog && shipmentLog.device) ? typeof shipmentLog.device === "object" ? shipmentLog.device.id : shipmentLog.device : undefined;
                        delete localShipmentLogs[shipmentLogId];
                        const groupedShipments = getUpdatedGroupedShipments(localShipmentLogs);
                        // possible that localShipmentLogs had N deviceIds with shipment logs, but since log is being removed groupedShipments could have less device ids
                        if (groupedShipments) {
                            const deviceIds = Object.keys(groupedShipments);
                            if (deletedDeviceId && !deviceIds.some(key => key === deletedDeviceId)) {
                                this.store.dispatch(ShipmentActions.clearDeviceShipments(deletedDeviceId));
                            }
                            deviceIds.forEach(deviceId => {
                                this.store.dispatch(ShipmentActions.setShipments({ keyId: deviceId, shipments: groupedShipments[deviceId] }));
                            });
                        }
                        dispatcher.trigger(DispatcherEvents.SyncShipments);
                    }
                }
            })
            .catch(handleApi);
    }

    private syncUserHosRuleSet(context: Context, e: LogEvent): Promise<void> {
        let apiUser = {...context.api.getUser()};
        let apiUserId = apiUser ? apiUser.id : undefined;
        let ruleSetId = e.data[0];

         return this.sync(context, [SyncType.UserHosRuleSet])
            .then(() => {
                let storageLogs = apiUserId && this.store.getState().userHosRuleSets[apiUserId];
                let localLogs = convertStorageRulesetsToDictionary(storageLogs);
                if (ruleSetId && e.verb === "Remove" && apiUser && localLogs && Object.keys(localLogs).length > 0) {
                    delete localLogs[ruleSetId];
                    apiUserId && this.store.dispatch(UserHosRuleSetActions.setUserHosRuleSets(
                        {
                            userId: apiUserId.toString(),
                            userHosRuleSets: localLogs
                        }
                    ));
                    dispatcher.trigger(DispatcherEvents.SyncUserHosRuleSet);
                }
            })
            .catch(handleApi);
    }

    private addDutyStatusLog(log: DutyStatusLog, syncLater = false) {
        this.applyActionToAllContexts((user, context) => {
            if (user && user.id && user.id === log.driver.id) {
                void context.sync.dutyStatusLogs.addItem(log, syncLater);
            }
        });
    }

    private setDutyStatusLog(log: DutyStatusLog) {
        this.applyActionToAllContexts((user, context) => {
            if (user && user.id && user.id === log.getDriverId()) {
                void context.sync.dutyStatusLogs.setItem(log);
            }
        });
    }

    /**
     * Handles the switch driver if the active driver was removed
     * @param userName The username to switch
     * @param isVehicleSelect If was performed on the vehicle select page
     * @param isRemoveActiveDriver If the switch was due to a removed active driver
     */
    private onSwitchActiveDriver(userName: string, isVehicleSelect: boolean, isRemoveActiveDriver: boolean) {
        if (isVehicleSelect || !isRemoveActiveDriver) {

            // Don't care about the vehicle select or if this was just a normal switch between 2 active users
            return;
        }

        // Do the switch on the current active context
        let activeContext = this.getContext();
        activeContext && activeContext.vehicleService.safeSetDriverVehicle(userName, null, true);
    }

    /**
     * Start pinging device GPS every ~min which is fallback for websocket server events
     * for driving state. We runTask first so we can determine GPS state before deciding
     * whether we should continue based on online and background status
     */
    private setupMonitorPosition() {
        let manageTaskByAppStatus = () => {
            let mobileState = this.store.getState().mobile;
            if (mobileState.online !== NetworkStates.ONLINE && !mobileState.background) {
                scheduler.start("@monitorPosition");
            } else {
                scheduler.stop("@monitorPosition");
            }
        };

        let runTask = () => {
            void this.movement.monitorPosition.bind(this.movement)();
            manageTaskByAppStatus();
        };

        scheduler.registerTask("@monitorPosition", runTask, constants.DRIVING.TRACKING).start("@monitorPosition");

        observeStore(this.store, state => state.mobile.online, manageTaskByAppStatus);
        observeStore(this.store, state => state.mobile.background, manageTaskByAppStatus);
    }

    /**
     * Returns the first active context whose user has stored credentials, for use in global singleton tasks.
     * Skipping credential-less contexts prevents background tasks from dispatching ApiNeedsCredentials
     * against the UI when the first co-driver context has no in-memory credentials (MYG-135664).
     * Using a dynamic getter rather than a captured closure prevents stale-context issues
     * when the user who originally registered a task logs out.
     */
    private getSharedTaskContext(): Context | undefined {
        const driveAPICredentials = this.store.getState().driveAPICredentials;
        const activeUserNames = Object.keys(this.activeContexts);
        for (const userName of activeUserNames) {
            if (driveAPICredentials?.[userName]) {
                return this.activeContexts[userName];
            }
        }
        if (activeUserNames.length > 0) {
            // This fires only in abnormal conditions (e.g. CLEAR_DRIVE_API_CREDENTIALS while a context
            // is still registered). Shared tasks are registered in startSchedulesAndServices, which only
            // runs after addContext has verified credentials via api.getCurrentCredentials() (backed by
            // driveAPICredentials in the store), and all of them are registered runLater=true — so the
            // login-flow window between context creation and credential storage is never reached.
            logger.error(logger.Source.SERVICES, `getSharedTaskContext: all ${ activeUserNames.length } active context(s) are credential-less; skipping shared task tick`);
        }
        return undefined;
    }

    private applyActionToAllContexts(action: (user: DeepReadonly<IUser> | null, context: Context) => void) {
        Object.keys(this.activeContexts).forEach(userName => {
            action(this.userService.getUserByName(userName), this.activeContexts[userName]);
        });
    }

    private enableServerEvents(context: Context) {
        context.serverEvents.on("dutystatuslog:change", (e: { userName: string})  => this.serverEventSyncHos(e));
        context.serverEvents.on("textmessage:change", (e: {messageContentType: string})  => this.syncMessages(context, e));
        context.serverEvents.on("dvirlog:change", (e: LogEvent) => this.syncDvirLogs(context, e));
        context.serverEvents.on("vehicledriving:change", (e: IVehicleDrivingEvent) => this.store.dispatch(DeviceMovementActions.setPubSubPayload(e)));
        context.serverEvents.on("shipmentlog:change", (e: LogEvent) => this.syncShipmentLogs(context, e));
        context.serverEvents.on("userhosruleset:change", (e: {e: LogEvent}) => this.syncUserHosRuleSet(context, e.e));
        context.serverEvents.on("trailerattachment:change", () => this.syncTrailerAttachments(context));
    }

    private disableServerEvents(context: Context) {
        context.serverEvents.off("dutystatuslog:change");
        context.serverEvents.off("textmessage:change");
        context.serverEvents.off("dvirlog:change");
        context.serverEvents.off("vehicledriving:change");
        context.serverEvents.off("shipmentlog:change");
        context.serverEvents.off("userhosruleset:change");
        context.serverEvents.off("trailerattachment:change");
    }
}
